import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UnconfirmedAttendanceCard } from "@/components/staff/AttendanceNudge";
import { PendingSwapsCard } from "@/components/staff/SwapRequests";
import {
  CalendarClock, Users, ClipboardList, Utensils, FileText, HeartPulse,
  AlertTriangle, CreditCard, ArrowRight, Clock, XCircle, MessageSquare,
  Calendar, RefreshCw, Loader2, Shield, Activity
} from "lucide-react";

const StaffOverview = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const h = { Authorization: `Bearer ${token}` };
      const [sRes, iRes] = await Promise.all([
        fetch(`${API}/staff/overview-stats`, { headers: h }),
        fetch(`${API}/staff/integrations/health`, { headers: h }),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (iRes.ok) setIntegrations(await iRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
    </div>
  );

  // Group cards into sections
  const urgentCards = [
    stats?.clinicalAlertsNew > 0 && {
      label: "Unacknowledged alerts",
      value: stats.clinicalAlertsNew,
      icon: AlertTriangle,
      color: "#EF4444",
      bg: "#FEF2F2",
      link: "/admin/follow-ups",
      urgent: true,
    },
    stats?.clinicalAlertsEscalated > 0 && {
      label: "Escalated alerts",
      value: stats.clinicalAlertsEscalated,
      icon: AlertTriangle,
      color: "#DC2626",
      bg: "#FEE2E2",
      link: "/admin/follow-ups",
      urgent: true,
    },
    stats?.failedComms > 0 && {
      label: "Failed communications",
      value: stats.failedComms,
      icon: XCircle,
      color: "#EF4444",
      bg: "#FEF2F2",
      link: "/admin/communications",
      urgent: true,
    },
  ].filter(Boolean);

  const primaryCards = [
    {
      label: "Today's consultations",
      value: stats?.todayConsultations || 0,
      icon: CalendarClock,
      color: "#2563EB",
      bg: "#EFF6FF",
      link: "/admin/consultations",
    },
    {
      label: "Upcoming appointments",
      value: stats?.upcomingAppointments || 0,
      icon: Calendar,
      color: "#0D9488",
      bg: "#F0FDFA",
      link: "/admin/consultations",
    },
    {
      label: "Total participants",
      value: stats?.total_participants || 0,
      icon: Users,
      color: "#059669",
      bg: "#ECFDF5",
      link: "/admin/patients",
    },
  ];

  const actionCards = [
    {
      label: "Paid, not booked",
      value: stats?.paidNotBooked || 0,
      icon: CreditCard,
      color: "#FF7A00",
      bg: "#FFF3ED",
      link: "/admin/programme",
      desc: "Participants who paid but have not booked a consultation yet",
    },
    {
      label: "Incomplete intake",
      value: stats?.incompleteIntake || 0,
      icon: ClipboardList,
      color: "#FF7A00",
      bg: "#FFF3ED",
      link: "/admin/assessments",
      desc: "Booked participants who have not completed their intake form",
    },
    {
      label: "Reports to approve",
      value: stats?.reportsPending || 0,
      icon: FileText,
      color: "#7C3AED",
      bg: "#F5F3FF",
      link: "/admin/reports",
      desc: "Clinical reports in draft or reviewed status",
    },
    {
      label: "Meal plans pending",
      value: stats?.mealPlansPending || 0,
      icon: Utensils,
      color: "#FF7A00",
      bg: "#FFF3ED",
      link: "/admin/meal-plans",
      desc: "Meal plans awaiting review or approval",
    },
    {
      label: "Follow-ups due",
      value: stats?.followUpsDue || 0,
      icon: HeartPulse,
      color: "#0D9488",
      bg: "#F0FDFA",
      link: "/admin/follow-ups",
      desc: "Check-ins sent and awaiting patient response",
    },
    {
      label: "Follow-ups overdue",
      value: stats?.followUpsOverdue || 0,
      icon: Clock,
      color: "#EF4444",
      bg: "#FEF2F2",
      link: "/admin/follow-ups",
      desc: "Check-ins overdue by 3+ days",
    },
    {
      label: "Approaching Week 12",
      value: stats?.approachingWeek12 || 0,
      icon: RefreshCw,
      color: "#7C3AED",
      bg: "#F5F3FF",
      link: "/admin/programme",
      desc: "Participants in Week 10 or later, nearing discharge",
    },
    {
      label: "Renewals pending",
      value: stats?.renewalsPending || 0,
      icon: CreditCard,
      color: "#0D9488",
      bg: "#F0FDFA",
      link: "/admin/programme",
      desc: "Six-week renewal payments awaiting completion",
    },
  ];

  return (
    <div className="space-y-6" data-testid="staff-overview">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Overview</h1>
          <p className="mt-1 text-base text-stone-500">Programme operations at a glance.</p>
        </div>
        <button onClick={() => { setLoading(true); fetchAll(); }} className="text-stone-400 hover:text-stone-600 transition-colors" data-testid="refresh-overview">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      <UnconfirmedAttendanceCard token={token} />
      <PendingSwapsCard token={token} />

      {/* Urgent Alerts */}
      {urgentCards.length > 0 && (
        <div className="flex flex-col gap-3">
          {urgentCards.map(card => (
            <Link key={card.label} to={card.link} className="block">
              <Card className="p-4 rounded-xl border-red-200 bg-red-50/50 flex items-center gap-3 hover:shadow-sm transition-all cursor-pointer" data-testid={`urgent-${card.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-red-100">
                  <card.icon className="w-[18px] h-[18px] text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-red-900">{card.value} {card.label}</p>
                </div>
                <Badge className="bg-red-100 text-red-700 text-xs">Needs attention</Badge>
                <ArrowRight className="w-4 h-4 text-red-300" />
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Primary Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {primaryCards.map(card => (
          <Link key={card.label} to={card.link}>
            <Card className="p-4 sm:p-5 rounded-xl border-stone-200/60 hover:border-stone-300 hover:shadow-sm transition-all cursor-pointer group" data-testid={`stat-${card.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-start justify-between">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: card.bg }}>
                  <card.icon className="w-[18px] h-[18px]" style={{ color: card.color }} />
                </div>
                <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-stone-500" />
              </div>
              <div className="mt-3">
                <p className="text-2xl sm:text-3xl font-extrabold text-stone-900">{card.value}</p>
                <p className="text-sm text-stone-500 mt-0.5">{card.label}</p>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Action Items */}
      <div>
        <h2 className="text-sm font-bold text-stone-500 uppercase tracking-wider mb-3">Requires action</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {actionCards.map(card => (
            <Link key={card.label} to={card.link}>
              <Card className={`p-4 rounded-xl border-stone-200/60 hover:border-stone-300 hover:shadow-sm transition-all cursor-pointer h-full ${card.value > 0 ? "" : "opacity-60"}`} data-testid={`action-${card.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: card.bg }}>
                    <card.icon className="w-3.5 h-3.5" style={{ color: card.color }} />
                  </div>
                  <span className={`text-xl font-extrabold ${card.value > 0 ? "text-stone-900" : "text-stone-400"}`}>{card.value}</span>
                </div>
                <p className="text-sm font-medium text-stone-900">{card.label}</p>
                <p className="text-xs text-stone-400 mt-0.5 line-clamp-2">{card.desc}</p>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Integration Status */}
      {integrations && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-stone-500 uppercase tracking-wider">Integrations</h2>
            <Link to="/admin/integrations" className="text-xs text-[#FF7A00] hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <Card className="rounded-xl border-stone-200/60 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-stone-400" />
              <span className="text-sm font-medium text-stone-700">{integrations.healthy}/{integrations.total} healthy</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {Object.entries(integrations.integrations).map(([key, val]) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <div className={`w-2 h-2 rounded-full ${val.status === "healthy" ? "bg-green-500" : val.status === "not_configured" ? "bg-stone-300" : "bg-amber-500"}`} />
                  <span className="text-stone-600 capitalize">{key.replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default StaffOverview;
